import React, { useState, useEffect } from "react";
import API_URL from "../../api/API";
import { useAdminAuthContext } from "../admin/useAdminAuthContext";

//const fieldTypes = ["checkbox", "radio", "text"];
const fieldTypes = ["checkbox", "radio"];
const optionTypes = ["proceed", "block", "warn"];

export const AdminEligibilityBuilderAll = ({ service }) => {
  const { admin } = useAdminAuthContext();

  const [questions, setQuestions] = useState([]);

  const [serviceIntro, setServiceIntro] = useState({
    adult_full: "",
    minor_full: "",
    adult_quick: "",
    minor_quick: "",
  });

  const [serviceWarnMessage, setServiceWarnMessage] = useState({
    adult_full: "",
    minor_full: "",
    adult_quick: "",
    minor_quick: "",
  });

  const [serviceBlockMessage, setServiceBlockMessage] = useState({
    adult_full: "",
    minor_full: "",
    adult_quick: "",
    minor_quick: "",
  });

  const [selectedQuestionIndex, setSelectedQuestionIndex] = useState(null);

  const emptyQuestion = {
    question_adult_full: "",
    question_minor_full: "",
    question_adult_quick: "",
    question_minor_quick: "",
    fieldType: "checkbox",
    options: [],
    addOther: false,
  };

  const [newQuestion, setNewQuestion] = useState({ ...emptyQuestion });
  const [optionLabel, setOptionLabel] = useState("");
  const [optionType, setOptionType] = useState("proceed");

  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchEligibilityQuestions = async () => {
      if (!service?.businessType?._id || !service?._id) return;

      try {
        const response = await fetch(
          API_URL.ADMIN_WAIVER_BUILDER_GET_ELIGIBILITY_ALL,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${admin.token}`,
              "x-session-id": admin.sessionId,
              "x-frontend-url": window.location.pathname,
            },
            body: JSON.stringify({
              businessTypeId: service.businessType._id,
              serviceId: service._id,
            }),
          }
        );

        const data = await response.json();
        
        if (!response.ok) throw new Error(data.message);

        if (data?.data) {
          setQuestions(
            (data.data.questions || []).map(q => ({
                ...q,
                options:
                q.options_adult_full ||
                q.options_adult_quick ||
                q.options_minor_full ||
                q.options_minor_quick ||
                []
            }))
          );

          setServiceIntro({
            adult_full: data.data.serviceIntro_adult_full || "",
            minor_full: data.data.serviceIntro_minor_full || "",
            adult_quick: data.data.serviceIntro_adult_quick || "",
            minor_quick: data.data.serviceIntro_minor_quick || "",
          });

          setServiceWarnMessage({
            adult_full: data.data.serviceWarnMessage_adult_full || "",
            minor_full: data.data.serviceWarnMessage_minor_full || "",
            adult_quick: data.data.serviceWarnMessage_adult_quick || "",
            minor_quick: data.data.serviceWarnMessage_minor_quick || "",
          });

          setServiceBlockMessage({
            adult_full: data.data.serviceBlockMessage_adult_full || "",
            minor_full: data.data.serviceBlockMessage_minor_full || "",
            adult_quick: data.data.serviceBlockMessage_adult_quick || "",
            minor_quick: data.data.serviceBlockMessage_minor_quick || "",
          });
        }
      } catch (error) {
        alert(error.message);
      }
    };

    fetchEligibilityQuestions();
  }, [service, admin.token]);

  /* OPTION MANAGEMENT */

  const addOption = () => {

    if (optionLabel.trim().toLowerCase() === "other") {
    setErrors({ optionLabel: "Use 'Add Other' toggle instead." });
    return;
    }

    if (!optionLabel.trim()) {
      setErrors({ optionLabel: "Option label required" });
      return;
    }

    setNewQuestion((prev) => ({
      ...prev,
      options: [...prev.options, { label: optionLabel, type: optionType }],
    }));

    setOptionLabel("");
    setOptionType("proceed");
    
    setErrors(prev => ({ ...prev, optionLabel: "" }));
  };

  const deleteOption = (idx) => {
    const removedOption = newQuestion.options[idx];
    setNewQuestion((prev) => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== idx),
      addOther: removedOption.label === "Other" ? false : prev.addOther
    }));
  };

  const toggleOther = () => {
    setNewQuestion(prev => {
        if (prev.addOther) {
        return {
            ...prev,
            addOther: false,
            options: prev.options.filter(opt => opt.label !== "Other")
        };
        }

        return {
        ...prev,
        addOther: true,
        options: [
            ...prev.options,
            {
            label: "Other",
            type: "warn",
            allowText: true,
            textConfig: { placeholder: "" }
            }
        ]
        };
    });
  };

  /* SAVE QUESTION */
  const saveQuestion = () => {

    if (!newQuestion.question_adult_full.trim()) {
        setErrors({ question_adult_full: "Adult Full question required" });
        return;
    }

    if (!newQuestion.question_minor_full.trim()) {
        setErrors({ question_minor_full: "Minor Full question required" });
        return;
    }

    if (!newQuestion.question_adult_quick.trim()) {
        setErrors({ question_adult_quick: "Adult Quick question required" });
        return;
    }

    if (!newQuestion.question_minor_quick.trim()) {
        setErrors({ question_minor_quick: "Minor Quick question required" });
        return;
    }

    const payload = {
        _id: newQuestion._id,
        question_adult_full: newQuestion.question_adult_full,
        question_minor_full: newQuestion.question_minor_full,
        question_adult_quick: newQuestion.question_adult_quick,
        question_minor_quick: newQuestion.question_minor_quick,
        fieldType: newQuestion.fieldType,

        // helper field for backend
        options: newQuestion.options,
        options_all: newQuestion.options
    };

    if (["checkbox", "radio"].includes(newQuestion.fieldType?.toLowerCase())) {

        payload.options_all = newQuestion.options.map(opt => ({
        label: opt.label,
        type: opt.type,
        message: opt.type === "block" ? (opt.message || "") : undefined,
        allowText: opt.label === "Other" ? true : opt.allowText || false,
        textConfig: {
            ...opt.textConfig,
            placeholder: opt.textConfig?.placeholder || ""
        }
        }));
    }

    const updated = [...questions];

    if (selectedQuestionIndex !== null) {
        updated[selectedQuestionIndex] = payload;
    } else {
        updated.push(payload);
    }

    setQuestions(updated);

    setNewQuestion({ ...emptyQuestion });
    setSelectedQuestionIndex(null);
    setOptionLabel("");
    setOptionType("proceed");

    setErrors({});
  };

  const editQuestion = (idx) => {
    setNewQuestion({
      ...questions[idx],
      options: questions[idx].options || [],
    });
    setSelectedQuestionIndex(idx);
  };

  const deleteQuestion = (idx) => {
    setQuestions((prev) => prev.filter((_, i) => i !== idx));
  };


  /** Update placeholder for "Other" */
  const updateOtherPlaceholder = (value) => {
    setNewQuestion(prev => ({
        ...prev,
        options: prev.options.map(opt =>
        opt.label === "Other"
            ? {
                ...opt,
                textConfig: {
                ...opt.textConfig,
                placeholder: value
                }
            }
            : opt
        )
    }));
  };

    /** Save all questions to server */
const saveToServer = async () => {

    if (questions.length === 0) {
        alert("Please add at least one question before saving.");
        return;
    }

    if (!service?.businessType?._id) {
        alert("Business Type is missing.");
        return;
    }

    setSaving(true);

    try {

      const formattedQuestions = questions.map(q => ({
        _id: q._id,

        question_adult_full: q.question_adult_full,
        question_minor_full: q.question_minor_full,
        question_adult_quick: q.question_adult_quick,
        question_minor_quick: q.question_minor_quick,

        fieldType: q.fieldType,

        options_type: q.options_type || "common_for_all",

        // helper field for backend expansion
        options_all: q.options || q.options_all || [],

        textConfig: q.fieldType === "text"
            ? { ...q.textConfig }
            : undefined
        }));


        const payload = {
            serviceId: service._id,
            businessTypeId: service.businessType._id,

            serviceIntro_adult_full: serviceIntro.adult_full,
            serviceIntro_minor_full: serviceIntro.minor_full,
            serviceIntro_adult_quick: serviceIntro.adult_quick,
            serviceIntro_minor_quick: serviceIntro.minor_quick,

            serviceWarnMessage_adult_full: serviceWarnMessage.adult_full,
            serviceWarnMessage_minor_full: serviceWarnMessage.minor_full,
            serviceWarnMessage_adult_quick: serviceWarnMessage.adult_quick,
            serviceWarnMessage_minor_quick: serviceWarnMessage.minor_quick,

            serviceBlockMessage_adult_full: serviceBlockMessage.adult_full,
            serviceBlockMessage_minor_full: serviceBlockMessage.minor_full,
            serviceBlockMessage_adult_quick: serviceBlockMessage.adult_quick,
            serviceBlockMessage_minor_quick: serviceBlockMessage.minor_quick,

            questions: formattedQuestions
        };


        const response = await fetch(
        API_URL.ADMIN_WAIVER_BUILDER_SAVE_ELIGIBILITY_ALL,
        {
            method: "POST",
            headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${admin.token}`,
            "x-session-id": admin.sessionId,
            "x-frontend-url": window.location.pathname,
            },
            body: JSON.stringify(payload),
        }
        );

        const data = await response.json();

        if (!response.ok)
        throw new Error(data.message || "Failed to save eligibility questions.");

        alert("Eligibility questions saved successfully!");

    } catch (error) {

        console.error(error);
        alert("Error saving eligibility questions: " + error.message);

    } finally {

        setSaving(false);

    }
  };

   /** Render saved questions */
const renderQuestionPreview = (q) => (
    <div className="border p-2 rounded mb-2 text-xs bg-white">
      <p className="font-semibold">(ADULT FULL Q): {q.question_adult_full}</p>
      <p className="font-semibold">(MINOR FULL Q): {q.question_minor_full}</p>
      <p className="font-semibold">(ADULT QUICK Q): {q.question_adult_quick}</p>
      <p className="font-semibold">(MINOR QUICK Q): {q.question_minor_quick}</p>
      {q.fieldType === "text" && (
        <input type="text" placeholder="Enter text" className="border p-1 rounded w-full text-xs mt-1" disabled />
      )}
      {["checkbox", "radio"].includes(q.fieldType) && (
        <div className="flex flex-col gap-1 mt-1">
          {(q.options || q.options_all || []).map((opt, idx) => (
            <div key={q._id || idx} className="flex items-center gap-1">
              <input type={q.fieldType} disabled />
              <label>{opt.label} ({opt.type})</label>
              {opt.label === "Other" && (
                    <input
                        type="text"
                        placeholder={opt.textConfig?.placeholder || "Enter text for 'Other'"}
                        value={opt.textConfig?.placeholder || ""}
                        onChange={(e) => updateOtherPlaceholder(e.target.value)}
                        disabled 
                        className="border p-1 rounded text-xs flex-1"
                    />
                )}

            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="flex gap-4 h-full">
      {/* Left panel: saved questions */}
      <div className="w-1/3 border p-2 rounded bg-gray-50 h-[500px] overflow-y-auto">
        <h3 className="font-semibold mb-2 text-sm">Preview</h3>

        {/* Intro preview */}
      {(serviceIntro.adult_full ||
        serviceIntro.adult_quick ||
        serviceIntro.minor_full ||
        serviceIntro.minor_quick)  && (
        <div className="border p-2 rounded mb-2 text-xs bg-blue-50 text-blue-700">
          <strong>Intro (Adult Full):</strong>
          <p>{serviceIntro.adult_full}</p>

           <strong>Intro (Minor Full):</strong>
          <p>{serviceIntro.minor_full}</p>

          <strong>Intro (Adult Quick):</strong>
          <p>{serviceIntro.adult_quick}</p>
         
          <strong>Intro (Minor Quick):</strong>
          <p>{serviceIntro.minor_quick}</p>

        </div>
      )}

        {questions.length === 0 ? (
          <p className="text-gray-500 text-xs">No questions added yet</p>
        ) : (
          questions.map((q, idx) => (
            <div key={q._id || idx} className="relative">
              {renderQuestionPreview(q)}
              <div className="flex justify-end gap-1 text-xs mb-2">
                <button onClick={() => editQuestion(idx)} className="text-blue-500">Edit</button>
                <button onClick={() => deleteQuestion(idx)} className="text-red-500">Delete</button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Right panel: question builder form */}
      <div className="flex-1 border p-3 rounded bg-white h-[500px] overflow-y-auto">

        {/* 🔥 Service Intro */}
        <div className="mb-4 p-3 rounded bg-gray-50 border">
            {/* <h4 className="text-sm font-semibold mb-2">Service Intro</h4>*/}
            <div className="mb-3">
            <label className="block text-xs font-semibold mb-1">Service Intro (Adult Full)</label>
            <textarea
                value={serviceIntro.adult_full}
                onChange={(e) => setServiceIntro({ ...serviceIntro, adult_full: e.target.value })}
                placeholder="Introductory paragraph (can include rich text later)"
                className="w-full border p-1 rounded text-xs"
                rows={3}
            />
            </div>
            <div className="mb-3">
            <label className="block text-xs font-semibold mb-1">Service Intro (Minor Full)</label>
            <textarea
                value={serviceIntro.minor_full}
                onChange={(e) => setServiceIntro({ ...serviceIntro, minor_full: e.target.value })}
                placeholder="Introductory paragraph (can include rich text later)"
                className="w-full border p-1 rounded text-xs"
                rows={3}
            />
            </div>
            <div className="mb-3">
            <label className="block text-xs font-semibold mb-1">Service Intro (Adult Quick)</label>
            <textarea
                value={serviceIntro.adult_quick}
                onChange={(e) => setServiceIntro({ ...serviceIntro, adult_quick: e.target.value })}
                placeholder="Introductory paragraph (can include rich text later)"
                className="w-full border p-1 rounded text-xs"
                rows={3}
            />
            </div>
            <div className="mb-3">
            <label className="block text-xs font-semibold mb-1">Service Intro (Minor Quick)</label>
            <textarea
                value={serviceIntro.minor_quick}
                onChange={(e) => setServiceIntro({ ...serviceIntro, minor_quick: e.target.value })}
                placeholder="Introductory paragraph (can include rich text later)"
                className="w-full border p-1 rounded text-xs"
                rows={3}
            />
            </div>
        </div>
        
        <div className="mb-4 p-3 rounded bg-blue-50 border">
            <div className="mb-3">
            <label className="block text-xs font-semibold mb-1">Service Warn Message  (Adult Full)</label>
            <textarea
                value={serviceWarnMessage.adult_full}
                onChange={(e) => setServiceWarnMessage({...serviceWarnMessage, adult_full: e.target.value})}
                placeholder="Service-level warn message"
                className="w-full border p-1 rounded text-xs"
                rows={2}
            />
            </div>
            <div className="mb-3">
            <label className="block text-xs font-semibold mb-1">Service Warn Message  (Minor Full)</label>
            <textarea
                value={serviceWarnMessage.minor_full}
                onChange={(e) => setServiceWarnMessage({...serviceWarnMessage, minor_full: e.target.value})}
                placeholder="Service-level warn message"
                className="w-full border p-1 rounded text-xs"
                rows={2}
            />
            </div>
            <div className="mb-3">
            <label className="block text-xs font-semibold mb-1">Service Warn Message  (Adult Quick)</label>
            <textarea
                value={serviceWarnMessage.adult_quick}
                onChange={(e) => setServiceWarnMessage({...serviceWarnMessage, adult_quick: e.target.value})}
                placeholder="Service-level warn message"
                className="w-full border p-1 rounded text-xs"
                rows={2}
            />
            </div>
            <div className="mb-3">
            <label className="block text-xs font-semibold mb-1">Service Warn Message  (Minor Quick)</label>
            <textarea
                value={serviceWarnMessage.minor_quick}
                onChange={(e) => setServiceWarnMessage({...serviceWarnMessage, minor_quick: e.target.value})}
                placeholder="Service-level warn message"
                className="w-full border p-1 rounded text-xs"
                rows={2}
            />
            </div>
        </div>
        

        {/* Service Block Message 🔥 */}
        <div className="mb-4 p-3 rounded bg-gray-50 border">
            <div className="mb-3">
            <label className="block text-xs font-semibold mb-1">Service Block Message  (Adult Full)</label>
            <textarea
                value={serviceBlockMessage.adult_full}
                onChange={(e) => setServiceBlockMessage({...serviceBlockMessage,adult_full: e.target.value})}
                placeholder="Service-level block message"
                className="w-full border p-1 rounded text-xs"
                rows={2}
            />
            </div>
            <div className="mb-3">
            <label className="block text-xs font-semibold mb-1">Service Block Message  (Minor Full)</label>
            <textarea
                value={serviceBlockMessage.minor_full}
                onChange={(e) => setServiceBlockMessage({...serviceBlockMessage,minor_full: e.target.value})}
                placeholder="Service-level block message"
                className="w-full border p-1 rounded text-xs"
                rows={2}
            />
            </div>
            <div className="mb-3">
            <label className="block text-xs font-semibold mb-1">Service Block Message  (Adult Quick)</label>
            <textarea
                value={serviceBlockMessage.adult_quick}
                onChange={(e) => setServiceBlockMessage({...serviceBlockMessage,adult_quick: e.target.value})}
                placeholder="Service-level block message"
                className="w-full border p-1 rounded text-xs"
                rows={2}
            />
            </div>
            <div className="mb-3">
            <label className="block text-xs font-semibold mb-1">Service Block Message  (Minor Quick)</label>
            <textarea
                value={serviceBlockMessage.minor_quick}
                onChange={(e) =>setServiceBlockMessage({...serviceBlockMessage,minor_quick: e.target.value})}
                placeholder="Service-level block message"
                className="w-full border p-1 rounded text-xs"
                rows={2}
            />
            </div>
        </div>
      
        <div className="mb-4 p-3 rounded bg-blue-50 border">
            <div className="mb-2">
            <label className="block text-xs font-semibold mb-1">Question (Adult Full)</label>
            <input
                type="text"
                value={newQuestion.question_adult_full}
                onChange={(e) => setNewQuestion({ ...newQuestion, question_adult_full: e.target.value })}
                placeholder="Enter question"
                className={`w-full border p-1 rounded text-xs ${errors.question_adult_full ? "border-red-500" : "border-gray-300"}`}
            />
            {errors.question_adult_full  && <p className="text-red-500 text-xs mt-1">{errors.question_adult_full}</p>}
            </div>
            <div className="mb-2">
            <label className="block text-xs font-semibold mb-1">Question (Minor Full)</label>
            <input
                type="text"
                value={newQuestion.question_minor_full}
                onChange={(e) => setNewQuestion({ ...newQuestion, question_minor_full: e.target.value })}
                placeholder="Enter question"
                className={`w-full border p-1 rounded text-xs ${errors.question_minor_full ? "border-red-500" : "border-gray-300"}`}
            />
            {errors.question_minor_full && <p className="text-red-500 text-xs mt-1">{errors.question_minor_full}</p>}
            </div>
            <div className="mb-2">
            <label className="block text-xs font-semibold mb-1">Question (Adult Quick)</label>
            <input
                type="text"
                value={newQuestion.question_adult_quick}
                onChange={(e) => setNewQuestion({ ...newQuestion, question_adult_quick: e.target.value })}
                placeholder="Enter question"
                className={`w-full border p-1 rounded text-xs ${errors.question_adult_quick ? "border-red-500" : "border-gray-300"}`}
            />
            {errors.question_adult_quick && <p className="text-red-500 text-xs mt-1">{errors.question_adult_quick}</p>}
            </div>
            <div className="mb-2">
            <label className="block text-xs font-semibold mb-1">Question (Minor Quick)</label>
            <input
                type="text"
                value={newQuestion.question_minor_quick}
                onChange={(e) => setNewQuestion({ ...newQuestion, question_minor_quick: e.target.value })}
                placeholder="Enter question"
                className={`w-full border p-1 rounded text-xs ${errors.question_minor_quick ? "border-red-500" : "border-gray-300"}`}
            />
            {errors.question_minor_quick && <p className="text-red-500 text-xs mt-1">{errors.question_minor_quick}</p>}
            </div>
        </div>

        <div className="mb-2">
          <label className="block text-xs font-semibold mb-1">Field Type</label>
          <select
            value={newQuestion.fieldType}
            onChange={(e) => setNewQuestion({ ...newQuestion, fieldType: e.target.value })}
            className="border p-1 rounded text-xs w-full"
          >
            {fieldTypes.map((ft) => (
              <option key={ft} value={ft}>{ft}</option>
            ))}
          </select>
        </div>

        {["checkbox", "radio"].includes(newQuestion.fieldType) && (
          <div className="mb-2">
            <label className="block text-xs font-semibold mb-1">Options</label>

            {/* Add new option */}
            <div className="flex gap-1 mb-1">
              <input
                type="text"
                value={optionLabel}
                onChange={(e) => setOptionLabel(e.target.value)}
                placeholder="Option label"
                className={`border p-1 rounded text-xs flex-1 ${errors.optionLabel ? "border-red-500" : "border-gray-300"}`}
              />
              <select value={optionType} onChange={(e) => setOptionType(e.target.value)} className="border p-1 rounded text-xs">
                {optionTypes.map((ot) => (
                  <option key={ot} value={ot}>{ot}</option>
                ))}
              </select>
              <button onClick={addOption} className="px-2 py-1 bg-blue-500 text-white text-xs rounded">Add</button>
            </div>
            {errors.optionLabel && <p className="text-red-500 text-xs mt-1">{errors.optionLabel}</p>}

            {/* Show added options in the form so you can delete them */}
            <div className="flex flex-col gap-1 mt-1">
              {newQuestion.options?.map((opt, idx) => (
                <div key={idx} className="flex items-center gap-1">
                  
                  <button
                  onClick={() => deleteOption(idx)}
                  className="ml-2 px-2 py-0.5 bg-red-500 text-white text-xs rounded hover:bg-red-600"
                  title="Delete option"
                >
                  ✕
                </button>
                  <input type={newQuestion.fieldType} disabled />

                  <label className="flex-1">{opt.label} ({opt.type})</label>
                  
                  {opt.label === "Other" && (
                    <input
                      type="text"
                      placeholder="Enter text for 'Other'"
                      value={opt.textConfig?.placeholder || ""}
                      onChange={(e) => updateOtherPlaceholder(e.target.value)}
                      className="border p-1 rounded text-xs flex-1"
                    />
                  )}

                </div>
              ))}
            </div>

            {/* Toggle Other */}
            <div className="flex items-center mt-1">
              <input type="checkbox" checked={newQuestion.addOther} onChange={toggleOther} id="addOther" className="mr-1" />
              <label htmlFor="addOther" className="text-xs">Add "Other" option (warn)</label>
            </div>
          </div>
        )}

        <button onClick={saveQuestion} className="mt-2 px-3 py-1 bg-green-500 text-white text-xs rounded">
          {selectedQuestionIndex !== null ? "Save Question" : "Add New Question"}
        </button>

        <button
          onClick={saveToServer}
          disabled={saving}
          className="mt-2 mr-2 px-3 py-1 bg-green-500 text-white text-xs rounded"
        >
          {saving ? "Saving..." : "Save Full Template"}
        </button>
      </div>
    </div>
  );
};
